/**
 * Technology Capabilities List Route
 *
 * Displays searchable, filterable list of technology capabilities
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useAllTags, useTechnologyCapabilities } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { TechnologyCapability } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/technology-capabilities/')({
  component: TechnologyCapabilitiesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { TechnologyCapabilitiesListPage };

interface TechnologyCapabilityFilters {
  search: string;
  domainId: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function TechnologyCapabilitiesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<TechnologyCapabilityFilters>({
    search: '',
    domainId: '',
  });

  // Fetch technology capabilities with GraphQL
  const { data, loading, error } = useTechnologyCapabilities();

  // Extract technology capabilities from connection
  const technologyCapabilities =
    data?.technologyCapabilities?.edges?.map((edge) => edge.node) || [];

  // Tags are now catalog tag ids selected via TagPicker (issue #764), not
  // free-typed strings — resolve to tag names for display instead of
  // showing raw ids.
  const { tags: allTags } = useAllTags();
  const tagNameById = new Map(allTags.map((tag) => [tag.entityId, tag.name]));

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<TechnologyCapability>({
      items: technologyCapabilities,
      page,
      pageSize,
      filterFn: (technologyCapability) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = technologyCapability.name.toLowerCase().includes(searchLower);
          const matchesDescription = technologyCapability.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.domainId && technologyCapability.domainId !== filters.domainId) {
          return false;
        }
        return true;
      },
      sortValue: (technologyCapability, column) =>
        String(technologyCapability[column as keyof TechnologyCapability] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<TechnologyCapability>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '45%',
      render: (technologyCapability) => (
        <div>
          <div className="entity-list-name-cell">{technologyCapability.name}</div>
          {technologyCapability.description && (
            <div className="entity-list-description-cell">
              {technologyCapability.description.substring(0, 60)}
              {technologyCapability.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'domainId',
      label: 'Domain',
      sortable: true,
      width: '30%',
      render: (technologyCapability) => <span>{technologyCapability.domainId || '—'}</span>,
    },
    {
      key: 'tags',
      label: 'Tags',
      sortable: false,
      width: '25%',
      render: (technologyCapability) => (
        <span>
          {technologyCapability.tags && technologyCapability.tags.length > 0
            ? technologyCapability.tags.map((tagId) => tagNameById.get(tagId) ?? tagId).join(', ')
            : '—'}
        </span>
      ),
    },
  ];

  const handleRowClick = (technologyCapability: TechnologyCapability): void => {
    navigate({ to: `/technology-capabilities/${technologyCapability.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<TechnologyCapabilityFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/technology-capabilities', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', domainId: '' });
    void navigate({ to: '/technology-capabilities', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/technology-capabilities', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: newPageSize } });
  };

  if (error) {
    return (
      <div className="technology-capabilities-list-page">
        <h1 className="forge-typography--headline4">Technology Capabilities</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load technology capabilities"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="technology-capabilities-list-page">
      <div className="technology-capabilities-header">
        <div>
          <h1 className="forge-typography--headline4">Technology Capabilities</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} technology capabilities`}
          </p>
        </div>
        <PermissionGate permission="technology-capability:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/technology-capabilities/new' })}
          >
            Create Technology Capability
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search technology capabilities..."
            aria-label="Search technology capabilities"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by domain"
          value={filters.domainId}
          onChange={(e) => handleFilterChange({ domainId: e.target.value })}
        >
          <option value="">All Domains</option>
          {Array.from(
            new Set(
              technologyCapabilities
                .map((technologyCapability) => technologyCapability.domainId)
                .filter((domainId): domainId is string => Boolean(domainId))
            )
          ).map((domainId) => (
            <option key={domainId} value={domainId}>
              {domainId}
            </option>
          ))}
        </select>

        {(filters.search || filters.domainId) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="technology-capabilities-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={3} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(technologyCapability) => technologyCapability.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No technology capabilities found',
                description:
                  filters.search || filters.domainId
                    ? 'Try adjusting your filters'
                    : 'Create your first technology capability to get started',
                actionLabel: 'Create Technology Capability',
                onAction: () => navigate({ to: '/technology-capabilities/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
